import { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";

import SizePills from "./size-pills/index";
import { fetchAvailability as fetchMembershipAvailability } from "slices/pdp-membership-slice";
import { fetchAvailability as fetchBuyAvailability, fetchPricing as fetchBuyPricing } from "slices/pdp-buy-slice";
import useMembershipError from "./use-membership-error";
import AddToBagError from "./add-to-bag-error";
import AddToBag from "./add-to-bag/index";
import AtomErrorNotification from "components/source/atoms/atom-error-notification";
import MembershipAddSwapCTA from "components/source/membership/membership-add-swap-cta";
import { Faq } from "routes";
import { isBagFullCe, hasAllItemsAtHome, hasAtLeastOneReturnPromise } from "helpers/membership-helpers";
import { priceStringIntoFloat } from "helpers/invoice-helper";
import RTRRadioInputPair from "components/design-system/radio-input-pair";
import FitRecommendation from "components/source/membership/fit/fit-recommendation";
import PromoExclusion from "components/source/products/promo-exclusion";
import CustomerPromise from "../shared/customer-promise";
import FitGuarantee from "../shared/fit-guarantee/fit-guarantee";
import { ATB_ERROR_ID, membershipLens, ASSORTMENTS, flagsAndExperimentNames } from "rtr-constants";
import AddressZipUpdater from "./address-zip-updater";
import { setSelectedSizeSku, setPDPBuyBoxMode } from "slices/pdp-slice";
import { isSelectedSizeUnavailable, hasNoAvailableSkus } from "./helpers/availability-helper";
import { isNotifyMeAvailableForNewPDP } from "helpers/notify-me-helpers";
import { isOneSize } from "./helpers/disco-product-helper";
import { shouldExcludePromo } from "helpers/product-drawer-helper";

const MembershipBuyBox = () => {
  const [buyBoxMode, setBuyBoxMode] = useState("membership");
  const [selectedSize, setSelectedSize] = useState(null);
  const staticProduct = useSelector(state => state.staticProduct);
  const membershipState = useSelector(state => state.membershipState);
  const ceBagCount = useSelector(state => state.ceBagCount);
  const depotId = useSelector(state => state.workingFilters?.depotId);

  const membershipStateLoading = useSelector(state => state.membershipStateLoading);

  const buyPricing = useSelector(state => state.pdp?.buy?.pricing);
  /* TODO: Remove hardcoding of depotId when we figure out filtering mechanism based on location */
  const membershipAvailability = useSelector(state => ({
    availability: state.pdp?.membership?.availability?.items,
    availabilityLoaded: state.pdp?.membership?.availabilityLoaded,
    availabilityError: state.pdp?.membership?.availabilityError,
  }));

  const buyAvailability = useSelector(state => ({
    availability: state.pdp?.buy?.availability?.items,
    availabilityLoaded: state.pdp?.buy?.availabilityLoaded,
    availabilityError: state.pdp?.buy?.availabilityError,
  }));
  const categoryRoot = useSelector(state => state.staticProduct?.categoryRoot);

  const itemUnavailable =
    hasNoAvailableSkus(buyAvailability, staticProduct.skus) &&
    hasNoAvailableSkus(membershipAvailability, staticProduct.skus);

  const promoExclusionFlag = useSelector(
    state => state.flagsAndExperiments?.[flagsAndExperimentNames.CATALOG_SERVICES_SHOW_PROMO_EXCLUSION]
  );

  const dispatch = useDispatch();
  const membershipError = useMembershipError();

  useEffect(() => {
    if (depotId && membershipStateLoading === false && membershipError?.id !== ATB_ERROR_ID.INELIGBLE_EARLY_ACCESS) {
      dispatch(fetchMembershipAvailability({ productId: staticProduct.productId, depotIds: depotId }));
    }
  }, [depotId, membershipStateLoading, membershipError?.id]);

  useEffect(() => {
    if (depotId) {
      dispatch(fetchBuyAvailability({ productId: staticProduct.productId, depotIds: depotId }));
    }
  }, [depotId]);

  useEffect(() => {
    dispatch(fetchBuyPricing({ productId: staticProduct.productId }));
  }, []);

  useEffect(() => {
    dispatch(setPDPBuyBoxMode(buyBoxMode));
  }, [buyBoxMode]);

  const handleSizeSelect = size => {
    setSelectedSize(size);
    if (size?.sku) {
      dispatch(setSelectedSizeSku(size.sku));
    }
  };

  const getMembershipPriceLabel = () => {
    const baseSlotUpgradePrice = membershipState?.baseSlotUpgradePrice || "USD 0.00";
    const allItemsAtHome = hasAllItemsAtHome(membershipState);
    const oneReturnPromise = hasAtLeastOneReturnPromise(membershipState);

    // FIXME: When lens-based logic is reintroduced to Universal PDP,
    // include lens check and membership discount logic here for pricing
    if (membershipState?.shipmentsCount > 0 && allItemsAtHome && !oneReturnPromise) {
      return "$0 to rent";
    }
    if (!isBagFullCe(membershipState, ceBagCount)) {
      return "$0 to rent";
    }
    let priceString = "$" + priceStringIntoFloat(baseSlotUpgradePrice);
    const suffix = " extra spot";
    return (priceString += suffix);
  };

  const getBuyPriceLabel = () => {
    const buyPrice = buyPricing?.price?.find(el => el.id === "buy");
    if (buyPrice?.value) {
      return `$${buyPrice.value} to buy`;
    }
  };

  const currentAvailability = buyBoxMode === "membership" ? membershipAvailability : buyAvailability;

  // Assume available until availability has loaded
  const hasAvailabilityLoaded = buyAvailability.availabilityLoaded && membershipAvailability.availabilityLoaded;
  const styleUnavailableInSelectedMode = hasNoAvailableSkus(
    buyBoxMode === "membership" ? membershipAvailability : buyAvailability,
    staticProduct.skus
  );

  const selectedSizeUnavailable = isSelectedSizeUnavailable(selectedSize?.sku, currentAvailability);
  const productIsEligbleForUser = membershipError?.id !== ATB_ERROR_ID.INELIGBLE_ITEM_DUE_TO_BASIC_PLAN;
  const oneSizeProduct = isOneSize(staticProduct);
  const isNotifyMeEligible = isNotifyMeAvailableForNewPDP(
    productIsEligbleForUser,
    styleUnavailableInSelectedMode,
    selectedSizeUnavailable,
    oneSizeProduct
  );

  const isWomensAccessories = categoryRoot === ASSORTMENTS.WOMENS_ACCESSORIES;
  const excludePromo = shouldExcludePromo({
    product: staticProduct,
    promoExclusionFlag,
  });

  return (
    <section className="mt-6" data-test-id="membership-buy-box" aria-labelledby="membership-add-to-bag">
      <SizePills
        skus={staticProduct.skus}
        availability={currentAvailability.availability}
        availabilityLoaded={currentAvailability.availabilityLoaded}
        availabilityError={currentAvailability.availabilityError}
        onSelect={handleSizeSelect}
        hideOneSize={isWomensAccessories}
        selectUnavailableSize={buyBoxMode === "membership" && isNotifyMeEligible}
      />

      <RTRRadioInputPair
        id="membership"
        dataTestId="membership-radio-input"
        name="pdp-radio"
        label={getMembershipPriceLabel()}
        value="membership"
        checked={hasAvailabilityLoaded && !itemUnavailable && buyBoxMode === "membership"}
        disabled={itemUnavailable}
        additionalClasses={{
          containerOverrides: "mt-6",
        }}
        onChange={() => setBuyBoxMode("membership")}
      />
      <RTRRadioInputPair
        id="buy"
        dataTestId="buy-radio-input"
        name="pdp-radio"
        label={getBuyPriceLabel()}
        value="buy"
        checked={hasAvailabilityLoaded && !itemUnavailable && buyBoxMode === "membership_buy"}
        disabled={itemUnavailable}
        additionalClasses={{
          containerOverrides: "mt-2",
        }}
        onChange={() => setBuyBoxMode("membership_buy")}
      />
      <PromoExclusion shouldExclude={excludePromo} containerClass="pl-7" />

      <FitRecommendation product={staticProduct} />

      {membershipError?.type === "swap_notification" && <MembershipAddSwapCTA membershipState={membershipState} />}

      {membershipError?.type === "notification" && buyBoxMode !== "membership_buy" && (
        <AtomErrorNotification
          additionalClassName={membershipError.additionalClassName}
          errorCopy={membershipError.text}
          errorCTA={membershipError.ctaText}
          errorURL={membershipError.errorURL}
          onClick={membershipError.onClickCta}
        />
      )}

      {!membershipError && styleUnavailableInSelectedMode && (
        <AtomErrorNotification
          additionalClassName={"available-soon"}
          errorCopy={"This item will be available soon! Heart it now so you can rent it later."}
          errorCTA={"Learn More"}
          errorURL={Faq.notAvailable}
        />
      )}

      <AddToBagError />
      <CustomerPromise />
      <FitGuarantee lens={membershipLens.unlimited} placement="pdp" />
      <AddToBag
        isBuy={buyBoxMode === "membership_buy"}
        membershipError={membershipError}
        availability={currentAvailability.availability}
        availabilityLoaded={currentAvailability.availabilityLoaded}
        selectedSize={selectedSize}
      />
      <AddressZipUpdater isGridProductDrawer={false} />
    </section>
  );
};

export default MembershipBuyBox;
